<template>
  <component
    :is="element"
    :class="{ 'stretched-link-container': isLink }"
    class="content-card"
    :href="customElementUrl || null"
  >
    <div class="content-card__media-container">
      <video-player
        v-if="video"
        :image="image || video.previewImage"
        :intersection-observer="{
          enable: true,
        }"
        :lazyload="lazyload"
        :loaded-animation="loadedAnimation"
        :sizes="sizes"
        :sources="video.sources"
        ratio="3:4"
      />

      <responsive-image
        v-else-if="image"
        class="content-card__image"
        :image="image"
        :ratio="imageRatio"
        :sizes="sizes"
        :lazyload="lazyload"
        :loaded-animation="loadedAnimation"
      />
    </div>

    <div class="content-card__footer">
      <p
        v-if="title"
        class="content-card__title"
        :class="titleClasses"
        v-text="title"
      />

      <p
        v-if="description"
        class="content-card__description text-body-xs-regular text-body-s-desktop"
        v-text="description"
      />

      <btn
        v-if="isLink"
        class="content-card__button"
        :class="{ 'stretched-link': isLink }"
        :label="ctaText"
        :class-label="ctaClassLabel"
        modifiers="text"
        :url="ctaUrl"
      />
    </div>
  </component>
</template>

<script>

/**
 * Vue: Content card (content-card)
 * -----------------------------------------------------------------------------
 * A card component to display generic content.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {String} [cardType] - card type for content, either 'regular',
 * 'article' or 'search'
 * @param {Array} [cards] - Array of cards for the carousel.
 * @param {String} [ctaUrl] - URL for the CTA.
 * @param {String} [ctaText] - Text for the CTA.
 * @param {String} [customElement] - Custom element either 'a' or 'div'.
 * @param {String} [customElementUrl] - Custom element URL.
 * @param {String} [ctaClassLabel] - Class for the CTA label.
 * @param {String} [description] - Text for the description.
 * @param {Object} [image] - Image object.
 * @param {Boolean} [lazyload] - Lazyload the image, defaults to true.
 * @param {Boolean} [loadedAnimation] - Applies image loaded animation.
 * @param {String} sizes - Value for sizes attribute.
 * @param {String} [title] - Title text.
 * @param {Boolean|Object} - Video object.
 */
import Btn from '~global/btn/btn.vue'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'

export default {
  name: 'ContentCard',

  components: {
    Btn,
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    cardType: {
      type: String,
      default: 'regular',
    },
    cards: {
      type: Array,
      default: () => [],
    },
    ctaUrl: {
      type: String,
      default: '',
    },
    ctaText: {
      type: String,
      default: '',
    },
    ctaClassLabel: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: () => ({}),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    loadedAnimation: {
      type: Boolean,
      default: true,
    },
    sizes: {
      type: String,
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    video: {
      type: [Boolean, Object],
      default: false,
    },
    customElement: {
      type: String,
      default: '',
    },
    customElementUrl: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Compute element type.
     * @returns {String}
     */
    element() {
      if (this.customElement) {
        return this.customElement
      }

      return this.customElementUrl ? 'a' : 'div'
    },

    /**
     * Compute CSS classes for the title element.
     * @returns {String}
     */
    titleClasses() {
      if (this.cardType === 'article') {
        return 'text-heading-m text-heading-l-desktop'
      }

      if (this.cardType === 'search') {
        return 'text-heading-m'
      }

      if (this.cardType === 'collection-quick-links') {
        return 'text-heading-s text-heading-s-desktop'
      }

      return 'text-heading-m text-heading-xl-desktop'
    },

    /**
     * Returns true if its image and has url.
     * @returns {Boolean}
     */
    isLink() {
      return this.ctaText && this.ctaUrl
    },

    /**
     * Compute image ratio.
     * @returns {String}
     */
    imageRatio() {
      let imageRatio = '3:4'

      if (this.cards?.length && this.cards?.length <= 2) {
        imageRatio = '5:2'
      }

      return imageRatio
    },
  },
}
</script>

<style lang="scss">
@import './content-card';
</style>
